import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Tabs,
  PeriodPicker,
  DashboardRangeBar,
  TabSection,
  TimezoneToggle,
  BROWSER_TIMEZONE,
  parseDashboardPeriod,
} from '@kioskgaming/ui';
import OverviewTab from './OverviewTab';
import FinancialTab from './FinancialTab';
import PlayersTab from './PlayersTab';
import OperationsTab from './OperationsTab';
import RoadblocksTab from './RoadblocksTab';
import { useAuth } from '../../hooks/useAuth';
import {
  useDashboardV2Financial,
  useDashboardV2Operations,
  useDashboardV2Overview,
  useDashboardV2Players,
  useDashboardV2Roadblocks,
} from '../../hooks/useDashboardV2';
import type { DashboardV2PeriodParams, ShopDashboardV2Envelope } from '../../services/api';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'financial', label: 'Financials' },
  { value: 'players', label: 'Players' },
  { value: 'operations', label: 'Operations' },
  { value: 'roadblocks', label: 'Roadblocks' },
];

/**
 * Performance analytics (Overview · Financials · Players · Operations · Roadblocks).
 * Rendered under the counter actions on the Dashboard at "/"; "/dashboard" redirects here (spec §3.2).
 */
export default function DashboardAnalytics() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = TABS.some((t) => t.value === searchParams.get('tab'))
    ? (searchParams.get('tab') as string)
    : 'overview';
  const useLocalTz = searchParams.get('tz') === 'local';
  const periodParams: DashboardV2PeriodParams = useMemo(() => {
    const period = parseDashboardPeriod(searchParams.get('period'));
    const dateFrom = searchParams.get('from') || undefined;
    const dateTo = searchParams.get('to') || undefined;
    return {
      period,
      ...(period === 'custom' && dateFrom ? { dateFrom } : {}),
      ...(period === 'custom' && dateTo ? { dateTo } : {}),
      ...(useLocalTz ? { tz: BROWSER_TIMEZONE } : {}),
    };
  }, [searchParams, useLocalTz]);

  // Overview stays enabled on every tab so the roadblocks badge can populate.
  const overviewQuery = useDashboardV2Overview(periodParams, true);
  const financialQuery = useDashboardV2Financial(periodParams, tab === 'financial');
  const playersQuery = useDashboardV2Players(periodParams, tab === 'players');
  const operationsQuery = useDashboardV2Operations(periodParams, tab === 'operations');
  const roadblocksQuery = useDashboardV2Roadblocks(periodParams, tab === 'roadblocks');

  const activeQuery =
    tab === 'overview'
      ? overviewQuery
      : tab === 'financial'
        ? financialQuery
        : tab === 'players'
          ? playersQuery
          : tab === 'operations'
            ? operationsQuery
            : roadblocksQuery;

  const roadblocksBadge = overviewQuery.data?.extras.roadblocksBadgeCount ?? 0;

  const tabsWithBadge = useMemo(
    () =>
      TABS.map((t) =>
        t.value === 'roadblocks' && roadblocksBadge > 0
          ? { ...t, label: `Roadblocks (${roadblocksBadge})` }
          : t,
      ),
    [roadblocksBadge],
  );

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const setPeriodParams = useCallback(
    (next: DashboardV2PeriodParams) => {
      if (next.period === 'custom' && (!next.dateFrom || !next.dateTo)) return;
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('period', next.period);
        if (next.period === 'custom' && next.dateFrom && next.dateTo) {
          params.set('from', next.dateFrom);
          params.set('to', next.dateTo);
        } else {
          params.delete('from');
          params.delete('to');
        }
        return params;
      });
    },
    [setSearchParams],
  );

  const costRate =
    user?.costRate != null && Number.isFinite(Number(user.costRate)) ? Number(user.costRate) : 0;

  return (
    <section aria-label="Performance" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <PeriodPicker
            value={periodParams.period}
            dateFrom={periodParams.dateFrom}
            dateTo={periodParams.dateTo}
            onChange={setPeriodParams}
            accent="blue"
          />
          <TimezoneToggle
            accent="blue"
            defaultLabel="Report time"
            useLocal={useLocalTz}
            onChange={(local) => {
              setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                if (local) params.set('tz', 'local');
                else params.delete('tz');
                return params;
              });
            }}
          />
        </div>
      </div>

      <DashboardRangeBar
        envelope={activeQuery.data as ShopDashboardV2Envelope | undefined}
        onRefresh={activeQuery.refresh}
        refreshing={activeQuery.refreshing || activeQuery.isFetching}
        accent="blue"
      />

      <Tabs items={tabsWithBadge} value={tab} onChange={setTab} accent="blue" />

      <TabSection
        error={activeQuery.error}
        onRetry={activeQuery.refetch}
        isLoading={activeQuery.isLoading}
      >
        {tab === 'overview' && (
          <OverviewTab
            period={periodParams.period}
            costRate={costRate}
            showWalletCard={false}
            data={overviewQuery.data}
            isLoading={overviewQuery.isLoading}
          />
        )}
        {tab === 'financial' && (
          <FinancialTab
            period={periodParams.period}
            data={financialQuery.data}
            isLoading={financialQuery.isLoading}
          />
        )}
        {tab === 'players' && (
          <PlayersTab
            period={periodParams.period}
            data={playersQuery.data}
            isLoading={playersQuery.isLoading}
          />
        )}
        {tab === 'operations' && (
          <OperationsTab
            period={periodParams.period}
            data={operationsQuery.data}
            isLoading={operationsQuery.isLoading}
          />
        )}
        {tab === 'roadblocks' && (
          <RoadblocksTab
            period={periodParams.period}
            data={roadblocksQuery.data}
            isLoading={roadblocksQuery.isLoading}
          />
        )}
      </TabSection>
    </section>
  );
}
